import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import {
  userSelector,
  currentOrganizationSelector,
  organizationsListSelector,
  orgTariffesSelector,
} from "../../store/selectors";
import "./ProfileScreen.css";
import ProfileSidebar from "../../components/ProfileSidebar/ProfileSidebar";
import LogoutIcon from "../../assets/logout.svg";
import DeleteProfileIcon from "../../assets/profile-delete.svg";
import Avatar from "../../components/Avatar/Avatar";
import AppLogo from "../../assets/AppLogoBlue.svg";
import { ReactComponent as MenuIcon } from "../../assets/mobile/menu.svg";
import OrganizationContextMenu from "../../components/OrganizationContextMenu/OrganizationContextMenu";
import arrowDown from "../../assets/arrowDown.svg";
import useIsMobile from "../../hooks/useIsMobile";
import toast from "react-hot-toast";
import {
  fetchCurrentUserForAvatar,
  setUserAvatar,
  updateUser,
} from "../../store/usersSlice";
import AddOrganizationModal from "../../components/AddOrganizationModal/AddOrganizationModal";
import { createOrganization } from "../../store/projectsSlice";
import EditOrganizationModal from "../../components/EditOrganizationModal/EditOrganizationModal";
import NotificationsPopup from "../../components/NotificationsPopup/NotificationsPopup";
import ProfilePopup from "../../components/ProfilePopup/ProfilePopup";

const ProfileScreen = () => {
  const user = useSelector(userSelector);
  const currentOrganization = useSelector(currentOrganizationSelector);
  const organizationsTariffes = useSelector(orgTariffesSelector);
  const organizations = useSelector(organizationsListSelector);
  const isMobile = useIsMobile();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { logout } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [firstNameValue, setFirstNameValue] = useState(
    user?.firstName || "Имя"
  );
  const [lastNameValue, setLastNameValue] = useState(
    user?.lastName || "Фамилия"
  );
  const [nicknameValue, setNicknameValue] = useState(
    user?.nickname || user?.email?.split("@")[0] || "nickname"
  );
  const [areChangesUnsaved, setAreChangesUnsaved] = useState(false);
  const userName = user?.email;
  const [showOrgMenu, setShowOrgMenu] = useState(false);
  const orgDropdownRef = React.useRef(null);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [isAddOrganizationModalOpen, setIsAddOrganizationModalOpen] =
    useState(false);
  const [organizationToRename, setOrganizationToRename] = useState(null);

  useEffect(() => {
    const redirectUrl = localStorage.getItem("redirectUrl");
    if (redirectUrl) {
      localStorage.removeItem("redirectUrl");
      navigate(redirectUrl);
    } else {
      document.title = "7cut • Профиль";
    }
  }, []);

  useEffect(() => {
    if (
      firstNameValue !== user.firstName ||
      lastNameValue !== user.lastName ||
      nicknameValue !== user.nickname
    ) {
      setAreChangesUnsaved(true);
    } else {
      setAreChangesUnsaved(false);
    }
  }, [firstNameValue, lastNameValue, nicknameValue]);

  React.useEffect(() => {
    function handleClickOutside(event) {
      if (
        orgDropdownRef.current &&
        !orgDropdownRef.current.contains(event.target)
      ) {
        setShowOrgMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleOrgSelect = (org) => {
    dispatch({ type: "SET_CURRENT_ORGANIZATION", payload: org });
    setShowOrgMenu(false);
  };

  const handleSaveChanges = () => {
    if (isSaving || !areChangesUnsaved) return;
    setIsSaving(true);
    dispatch(
      updateUser({
        firstName: firstNameValue,
        lastName: lastNameValue,
        nickname: nicknameValue,
      })
    ).then((res) => {
      if (!res.error) {
        toast.success("Изменения сохранены");
        setIsSaving(false);
        setAreChangesUnsaved(false);
      } else {
        const errors = res.payload.errors;
        for (let i = 0; i < errors.length; i++) {
          let error = errors[i];
          toast.error(error);
        }

        setIsSaving(false);
      }
    });
  };

  const handleClickAvatar = () => {};

  const handleFileUpload = (e) => {
    setAvatarLoading(true);
    const file = e.target.files[0];
    if (file && file.size <= 4096 * 1024 * 1024) {
      const formData = new FormData();
      formData.append("file", file);

      dispatch(setUserAvatar({ formData })).then((res) => {
        if (res.error) {
          toast.error(res.payload.error || res.payload);
          setAvatarLoading(false);
        } else {
          dispatch(fetchCurrentUserForAvatar()).then((res) => {
            setAvatarLoading(false);
          });
        }
      });
    } else {
      setAvatarLoading(false);
      toast.error("Ошибка загрузки аватара");
    }
  };

  const handleAddOrganization = () => {
    setIsAddOrganizationModalOpen(true);
  };

  const handleOrganizationCreated = (data) => {
    dispatch(createOrganization(data)).then((res) => {
      if (!res.error) {
        dispatch({
          type: "SET_CURRENT_ORGANIZATION",
          payload: res.payload,
        });
      }
    });
  };

  if (!user) return null;

  const handleRenameOrg = (org) => {
    setOrganizationToRename(org);
  };

  const handleOpenAdmin = () => {
    const answer = prompt("");
    if (answer === "nowember2025admin") {
      navigate("/profile/admin");
    }
  };

  return (
    <div className={isMobile ? "App-mobile" : "App"}>
      {/* HEADER */}
      <div
        className={isMobile ? "mainScreenHeader-mobile" : "mainScreenHeader"}
      >
        <div className={isMobile ? "header-left-mobile" : "header-left"}>
          {isMobile && (
            <div
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              style={{ display: "flex", marginTop: 7 }}
            >
              <MenuIcon color="white" />
            </div>
          )}
          <div
            onClick={() => navigate("/home", { replace: true })}
            className={"mainScreen-logoBlock"}
            style={{ cursor: "pointer" }}
          >
            <img src={AppLogo} style={{ height: "100%" }} alt="logo" />
          </div>
        </div>
        <div className="header-actions">
          <NotificationsPopup />
          <ProfilePopup />
        </div>
      </div>
      {/* LAYOUT: sidebar + main content */}
      <div
        className={isMobile ? "profile-container-mobile" : "profile-container"}
        style={{ alignItems: "flex-start" }}
      >
        {/* SIDEBAR */}
        {((!isMobile || isMenuOpen) && (
          <ProfileSidebar activeTab="profile" />
        )) ||
          null}

        {/* MAIN CONTENT */}
        <main className={isMobile ? "profile-main-mobile" : "profile-main"}>
          <h1 className="profile-title">Профиль</h1>
          <div className="profile-header">
            <label>
              <Avatar
                isLoading={avatarLoading}
                user={user}
                onClick={() => handleClickAvatar()}
                name={user.name || user.email}
                size={128}
              />
              <input
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                style={{ display: "none" }}
              />
            </label>
            <div className="profile-header-info">
              <div className="profile-header-name">
                {user.firstName || "Имя"} {user.lastName || "Фамилия"}
              </div>
              <div className="profile-header-username">
                @{user.nickname || user.email?.split("@")[0]}
              </div>
              <div
                className="profile-header-role"
                onDoubleClick={handleOpenAdmin}
              >
                {user.role || "Владелец"}
              </div>
            </div>
          </div>
          <div className={isMobile ? "profile-form-mobile" : "profile-form"}>
            <div
              className={
                isMobile ? "profile-form-left-mobile" : "profile-form-left"
              }
            >
              <label>
                Имя
                <input
                  type="text"
                  value={firstNameValue}
                  onChange={(e) => {
                    setFirstNameValue(e.target.value);
                  }}
                />
              </label>
              <label>
                Фамилия
                <input
                  type="text"
                  value={lastNameValue}
                  onChange={(e) => {
                    setLastNameValue(e.target.value);
                  }}
                />
              </label>
              <label>
                Электронная почта
                <input type="text" value={user.email || ""} />
              </label>
              <label>
                Никнейм
                <input
                  type="text"
                  value={nicknameValue}
                  onChange={(e) => {
                    setNicknameValue(e.target.value);
                  }}
                />
              </label>
              {isMobile && (
                <>
                  <label>
                    Мои организации
                    <div
                      ref={orgDropdownRef}
                      className="profile-org-dropdown"
                      onClick={() => setShowOrgMenu((v) => !v)}
                    >
                      <span className="profile-organization-text">
                        {currentOrganization?.title}
                      </span>
                      <img
                        src={arrowDown}
                        alt="arrow"
                        style={{
                          width: 6,
                          height: 6,
                          marginLeft: 8,
                          transform: showOrgMenu ? "rotate(180deg)" : "none",
                        }}
                      />
                      {showOrgMenu && (
                        <OrganizationContextMenu
                          isProfile={true}
                          organizations={organizations || []}
                          onSelect={handleOrgSelect}
                          onClose={(e) => {
                            e.stopPropagation();
                            setShowOrgMenu(false);
                          }}
                          onAddOrganization={() => handleAddOrganization()}
                          hideAddOrganization={true}
                          menuWidth={403}
                          setOrganizationToRename={handleRenameOrg}
                          menuRight={-17}
                          isOpen={showOrgMenu}
                        />
                      )}
                    </div>
                  </label>
                  <label className="profile-tariff-block">
                    Мой тариф
                    <div className="profile-tariff-row">
                      <span className="profile-tariff">
                        {(currentOrganization &&
                          organizationsTariffes[currentOrganization.id] &&
                          organizationsTariffes[currentOrganization.id].plan &&
                          organizationsTariffes[currentOrganization.id].plan
                            .name) ||
                          "Нет"}
                      </span>
                      <button
                        onClick={() => navigate("/pricing")}
                        className="profile-tariff-btn"
                      >
                        Смотреть тарифы
                      </button>
                    </div>
                  </label>
                  <div className="profile-tariff-block">
                    <button
                      onClick={handleSaveChanges}
                      className={`profile-tariff-btn ${
                        !areChangesUnsaved && "profile-tariff-btn-active"
                      }`}
                    >
                      {isSaving ? "Сохраняется..." : "Сохранить изменения"}
                    </button>
                  </div>
                </>
              )}
              <div className="profile-form-actions">
                <button onClick={logout} className="profile-logout">
                  <img
                    src={LogoutIcon}
                    alt="notifications"
                    stroke="green"
                    width={20}
                    style={{ marginRight: 5 }}
                    height={20}
                  />
                  Выйти из аккаунта
                </button>
                <button className="profile-delete">
                  <img
                    src={DeleteProfileIcon}
                    alt="notifications"
                    stroke="green"
                    width={20}
                    style={{ marginRight: 5 }}
                    height={20}
                  />
                  Удалить аккаунт
                </button>
              </div>
            </div>
            {!isMobile && (
              <div className="profile-form-right">
                <label>
                  Мои организации
                  <div
                    ref={orgDropdownRef}
                    className="profile-org-dropdown"
                    onClick={() => setShowOrgMenu((v) => !v)}
                  >
                    <span className="profile-organization-text">
                      {currentOrganization?.title}
                    </span>
                    <img
                      src={arrowDown}
                      alt="arrow"
                      style={{
                        width: 6,
                        height: 6,
                        marginLeft: 8,
                        transform: showOrgMenu ? "rotate(180deg)" : "none",
                      }}
                    />
                    {showOrgMenu && (
                      <OrganizationContextMenu
                        isProfile={true}
                        organizations={organizations || []}
                        onSelect={handleOrgSelect}
                        onClose={(e) => {
                          e.stopPropagation();
                          setShowOrgMenu(false);
                        }}
                        onAddOrganization={() => handleAddOrganization()}
                        hideAddOrganization={true}
                        menuWidth={403}
                        setOrganizationToRename={handleRenameOrg}
                        menuRight={-17}
                        isOpen={showOrgMenu}
                      />
                    )}
                  </div>
                </label>
                <label className="profile-tariff-block">
                  Мой тариф
                  <div className="profile-tariff-row">
                    <span className="profile-tariff">
                      {(currentOrganization &&
                        organizationsTariffes[currentOrganization.id] &&
                        organizationsTariffes[currentOrganization.id].plan &&
                        organizationsTariffes[currentOrganization.id].plan
                          .name) ||
                        "Нет"}
                    </span>
                    <button
                      onClick={() => navigate("/pricing")}
                      className="profile-tariff-btn"
                    >
                      Смотреть тарифы
                    </button>
                  </div>
                </label>
                <div className="profile-tariff-block">
                  <button
                    onClick={handleSaveChanges}
                    className={`profile-tariff-btn ${
                      !areChangesUnsaved && "profile-tariff-btn-active"
                    }`}
                  >
                    {isSaving ? "Сохраняется..." : "Сохранить изменения"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </main>
        {isAddOrganizationModalOpen && (
          <AddOrganizationModal
            isOpen={isAddOrganizationModalOpen}
            onClose={() => setIsAddOrganizationModalOpen(false)}
            onOrganizationCreated={handleOrganizationCreated}
          />
        )}
        {organizationToRename && (
          <EditOrganizationModal
            organization={organizationToRename}
            onClose={() => setOrganizationToRename(null)}
            onSave={(updatedOrg) => {
              setOrganizationToRename(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ProfileScreen;
